import { Card, Grid, GridSize } from '@mui/material';
import { ResponsiveStyleValue } from '@mui/system';
import { AlgoliaHit } from 'instantsearch.js';
import { Contract, Contract_Status_Enum, Custom_Price_List_Packaging, Maybe } from '@kheops/graphql';
import { useMemo } from 'react';
import { round } from '@kheops/utils';
import { ProductHit } from '../search';
import { DisplayMode } from '../state/state';
import ProductHitCard from '../../products/common/ProductHitCard';
import { mapAlgoliaHitToProductCardData } from '../../products/common/product-card.mappers';
import { productMSOPImageSizeByBreakpoint } from '../../discovery/state';
import SearchProductRow from './SearchProductRow';

export type SearchContract = Pick<Contract, 'status' | 'discount' | 'supplyingCompanyId' | 'buyingCompanyId' | 'reference_id' | 'order_count'> &
  {
    custom_price_list?: Maybe<{
      custom_price_list_packagings: Pick<Custom_Price_List_Packaging, 'company_id' | 'discount' | 'packaging_sku'>[]
    }>
  };

export interface SearchProductHitOptions {
  hideCompany?: boolean;
}
type SearchProductHitProps = SearchProductHitOptions
  & {
    hit: AlgoliaHit<ProductHit>;
    displayMode: DisplayMode;
    sizes?: { [key: string]: GridSize };
    contract?: SearchContract;
  }

export interface ProductHitInfo {
  price: string;
  productUrl: string;
  companyUrl: string;
}

export default function SearchProductHit({ hit, hideCompany, displayMode, sizes, contract }: SearchProductHitProps): React.JSX.Element {
  const contractSigned = contract?.status === Contract_Status_Enum.Signed;
  const adjustedHit = useMemo(() => {
    if (contract) {
      const customPriceListDiscount = contract.custom_price_list?.custom_price_list_packagings
        .find(({ packaging_sku }) => parseInt(hit.objectID.split('-')[0], 10) === packaging_sku)
        ?.discount;
      const discount = 1 - (customPriceListDiscount || contract.discount);

      return {
        ...hit,
        price: round(hit.price * discount),
        volumePrice: {
          ...hit.volumePrice,
          value: round(hit.volumePrice.value * discount),
        },
      };
    }

    return hit;
  }, [hit, contract]);

  const cardSizes = useMemo((): ResponsiveStyleValue<GridSize> => {
    return { xs: 12, sm: 6, md: 6, lg: hideCompany ? 3 : 4, xl: hideCompany ? 4 : 5, ...sizes };
  }, [sizes]);

  const product = useMemo(() => mapAlgoliaHitToProductCardData(adjustedHit), [adjustedHit]);

  return (
    displayMode === DisplayMode.GRID
      ? (
        <Grid key={hit.objectID} size={cardSizes}>
          <ProductHitCard
            product={product}
            displayCompany={!hideCompany}
            displayPackaging
            displayPackagingPrice={contractSigned}
            displaySuggestedRetailPrice={!contractSigned}
            displayBasketButton={contractSigned}
            displayWishlistButton={!contractSigned}
            productImageSizeByBreakPoint={productMSOPImageSizeByBreakpoint}
          />
        </Grid>
      )
      : (
        <Card sx={{ display: 'flex', width: '100%', p: 1.5, borderRadius: 3, boxShadow: '0 4px 15px 0 rgba(0, 0, 0, 0.03)' }}>
          <SearchProductRow hit={adjustedHit} contractStatus={contract?.status} />
        </Card>
      )
  );
}
